import type { GaiaWebSocketResponse } from "@expert/gaia";
import type { GaiaErrorBody, GaiaErrorMessage, ReactiveMessagePayload } from "@expert/shared-types";

import { handleIncomingMessage, sendFailureMetric, useTimelineStore } from "@asurion-core/expert-workspace-timeline";
import { gaiaWsEventBus, isReactiveErrorMessage, isReactiveMessage } from "@expert/gaia";
import { useEffect } from "react";

import { useLoggerWithContextStore } from "../state";

export const useGaiaMessageEvents = () => {
    const { loggerWithContext } = useLoggerWithContextStore();
    useEffect(() => {
        // Reactive Message Handling
        const gaiaEventBusMessageSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            ({ body }: GaiaWebSocketResponse) => {
                /*
                    The orchestrator invoke success event returns reactive,
                    proactive, and autocomplete messages. Both proactive and
                    reactive messages are being handled in this event handler.
                    Auto complete messages are being handled in the useGaiaAutoCompleteEvents
                    handler.
                */
                if (!isReactiveMessage(body as ReactiveMessagePayload)) return;
                handleIncomingMessage(body as ReactiveMessagePayload, loggerWithContext);

                // Clear retryPayload in case the orchestrator timeout fired before this response arrived
                const reactiveBody = body as ReactiveMessagePayload;
                useTimelineStore.getState().updateMessage(reactiveBody.sessionGroupId, reactiveBody.messageId, {
                    metadata: { retryPayload: undefined },
                });
            },
        );

        const gaiaEventBusOrchestratorFailure = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-error",
            ({ body }: GaiaWebSocketResponse) => {
                const errorMessage = body as GaiaErrorMessage;

                let errorBody: GaiaErrorBody;
                try {
                    errorBody = JSON.parse(errorMessage.body) as GaiaErrorBody;
                } catch {
                    loggerWithContext.error(
                        { module: "useGaiaMessageEvents", errorMessage },
                        "Failed to parse GAIA error body",
                    );
                    return;
                }

                loggerWithContext.warn(
                    {
                        module: "useGaiaMessageEvents",
                        errorMessage,
                        errorBody,
                    },
                    "GAIA websocket error message response",
                );
                if (isReactiveErrorMessage(errorBody)) {
                    sendFailureMetric("botFailure");
                    useTimelineStore.getState().updateMessage(errorBody.sessionGroupId, errorBody.messageId, {
                        metadata: {
                            retryPayload: {
                                text: errorBody.message,
                                type: "Expert",
                            },
                        },
                    });
                }
            },
        );

        // NOTE: We no longer support Proactive Messages in Expert Assist
        // We can enable this again if prioritized using the GAIA websocket & orchestrator

        return () => {
            gaiaEventBusMessageSuccess();
            gaiaEventBusOrchestratorFailure();
        };
    }, [loggerWithContext]);
};
